import { AddRounded, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton } from "@mui/material";
import { Fragment, SyntheticEvent, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { CBCreationOfProps, IFormValues } from "../../types";
import { defaultSalesAreasState, isGlobalEditModeState } from "../../state/customerbaseState";
import CBAccordionTitle from "../CBAccordionTitle";
import UpdateBtnsAccordion from "../UpdateBtnsAccordion";
import CBSalesAreasAccordion from "./CBSalesAreasAccordion";
import { getCountryByCode } from "../../utils";

const CustomerBaseSalesAreaDetail = ({creationOf} : CBCreationOfProps) => {

    const { t } = useTranslation('customerbase');
    const isCreationMode = !!creationOf;
    const { control, getValues } = useFormContext<IFormValues>();
    const [isExpanded, setIsExpanded] = useState(false);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const editing = isGlobalEditMode;
    const salesAreasFieldArray = useFieldArray<IFormValues, 'salesAreas'>({
        name: "salesAreas",
        control: control
    });

    const [filteredSalesAreas, setFilteredSalesAreas] = useState<string[]>([]);
    const [defaultSalesAreas, setDefaultSalesAreas] = useRecoilState(defaultSalesAreasState);

    const onAdd = (event: SyntheticEvent) => {
        event.stopPropagation();
        const newSalesArea = {
            salesOrganization: "",
            distributionChannel: "",
            division: "",
            currency: ""
        };
        salesAreasFieldArray.append(newSalesArea);
    }

    const onRemove = (event: SyntheticEvent, index: number) => {
        event.stopPropagation();
        salesAreasFieldArray.remove(index);
    }

    useEffect(() => {
        
        if(isCreationMode && !getValues("cbDetails.cbCustomerId")) return;

        if(defaultSalesAreas.length == 0) {
            setDefaultSalesAreas(salesAreasFieldArray.fields);

            let so: string[] = []
            salesAreasFieldArray.fields.forEach((area, index) => {
                if(!so.includes(area.salesOrganization)) {
                    if(area.salesOrganization == "")
                        so.unshift(area.salesOrganization);
                    else if(!!getValues(`salesAreas.${index}.id`))
                        so.push(area.salesOrganization);
                }
            });
            setFilteredSalesAreas(so);
        }

    }, [salesAreasFieldArray.fields, isCreationMode])

    return (
        <Accordion expanded={isExpanded} onChange={(_e, expanded) => {
            setIsExpanded(expanded);
        }}>
            <AccordionSummary expandIcon={ <ExpandMore/> }>
                { isCreationMode ?  
                    <Grid container alignItems="center" width={"98%"}>
                            <Grid item md={6} xs={6}>
                                <CBAccordionTitle
                                    section="salesAreas"
                                    label={t("tabs.accordionsLabels.cbSalesAreaDetails")}
                                />
                            </Grid>
                            <Grid item container justifyContent={"flex-end"} md={6} xs={6}>
                                <Grid item>
                                    <IconButton
                                        color={"accent"}
                                        disabled={!isExpanded}
                                        onClick={onAdd}
                                    >
                                        <AddRounded/>
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    :
                        <UpdateBtnsAccordion
                            section="salesAreas"
                            label={t("tabs.accordionsLabels.cbSalesAreaDetails")}
                            editing={editing}
                            isExpanded={isExpanded}
                            showAdd={editing}
                            onAdd={onAdd}
                        />
                }
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={1}>
                    { (isCreationMode && !getValues("cbDetails.cbCustomerId")) ?
                    
                        salesAreasFieldArray.fields.map((area, index) => (
                            <Grid item md={12} xs={12} key={`sales-area-detail-${area.id}`}>
                                <CBSalesAreasAccordion
                                    isExpanded={isExpanded}
                                    index={index}
                                    area={area}
                                    onRemove={onRemove}
                                    creationOf={creationOf}
                                />
                            </Grid>
                        ))
                    :
                        <>
                            {
                                salesAreasFieldArray.fields.map((area, index) => (
                                    (!getValues(`salesAreas.${index}.id`)) && 
                                    <Grid item md={12} xs={12} key={`sales-area-detail-${area.id}`}>
                                        <CBSalesAreasAccordion
                                            isExpanded={isExpanded}
                                            index={index}
                                            area={area}
                                            onRemove={onRemove}
                                            creationOf={creationOf}
                                        />
                                    </Grid>
                                ))
                            }
                            {
                                filteredSalesAreas.map((so) => (
                                    <Fragment key={`fragment-sales-area-country-${so}`}>
                                        <Grid item md={12} xs={12} key={`sales-area-country-${so}`}>
                                            {!!so && 
                                                <Accordion>
                                                    <AccordionSummary expandIcon={ <ExpandMore/>}>
                                                        {t(`labels.country.${getCountryByCode(so)}`, {defaultValue: so})}
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container rowSpacing={1}>
                                                            {
                                                                salesAreasFieldArray.fields.map((area, index) => (
                                                                    /**
                                                                     * Si sceglie di effettuare il controllo sulla salesOrganization tramite l'array defaultSalesAreas
                                                                     * poichè è necessario mantenere la clusterizzazione durante l'editing.
                                                                     * Essendo il campo in questione modificabile si perderebbe il riferimento per la clusterizzazione
                                                                     * nel caso in cui venga eliminata la salesOrganization senza sceglierne altra e aggiunta una nuova salesArea.
                                                                     * Si auspica in una migliore soluzione :) 
                                                                     */
                                                                    (!!getValues(`salesAreas.${index}.id`) && defaultSalesAreas[index]?.salesOrganization == so) && 
                                                                        <Grid item md={12} xs={12} key={`sales-area-detail-${area.id}`}>
                                                                            <CBSalesAreasAccordion
                                                                                isExpanded={isExpanded}
                                                                                index={index}
                                                                                area={area}
                                                                                onRemove={onRemove}
                                                                                creationOf={creationOf}
                                                                            />
                                                                        </Grid>
                                                                ))
                                                            }
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            }
                                        </Grid> 
                                    </Fragment>
                                ))
                            }
                        </>
                    }                  
                </Grid>
            </AccordionDetails>
        </Accordion>
    )

}

export default CustomerBaseSalesAreaDetail;